import "./style/AmazonSFPConfigListingsEdit.css"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Button, Container, Form, Modal, Table } from "react-bootstrap"
import { notify } from "../notify/notify"
import { useUserContext } from "../user/UserContext"
import { apiFetch } from "../utility/fetch"

interface Listing {
  listingName: string
  Id: string
  reason?: "Addition" | "Removal"
}

interface AzureListing {
  amazonListingSku: string
  listingId: string
}

interface AmazonSFPConfigListingsEditProps {
  configName: string
  listings: Listing[]
  visible: boolean
  azureListings: AzureListing[]
  closeModal: () => void
  saveConfig: (updatedListings: Listing[], configName: string) => void
}

const AmazonSFPConfigListingsEdit = ({
  configName,
  listings: initialListings,
  visible,
  azureListings,
  closeModal,
  saveConfig,
}: AmazonSFPConfigListingsEditProps) => {
  const { userData } = useUserContext()
  const [listings, setListings] = useState<Listing[]>([])
  const [addSku, setAddSku] = useState("")
  const [pasteData, setPasteData] = useState("")

  useEffect(() => {
    setListings(initialListings.map(({ listingName, Id }) => ({ listingName, Id })))
  }, [initialListings])

  const handlePaste = useCallback(() => {
    const rows = pasteData.trim().split("\n")
    const updatedListings = [...listings]
    const seen = new Set<string>()
    let changesMade = false

    rows.forEach((row) => {
      const sku = row.trim()
      if (!sku || seen.has(sku)) return
      seen.add(sku)

      const match = azureListings.find((a) => a.amazonListingSku === sku)
      if (!match) return notify("warning", `SKU not found in lookup: ${sku}`)

      const existing = updatedListings.find((l) => l.listingName === sku)

      if (existing) {
        if (!existing.reason) {
          existing.reason = "Removal"
          changesMade = true
        }
      } else {
        updatedListings.push({
          listingName: match.amazonListingSku,
          Id: match.listingId,
          reason: "Addition",
        })
        changesMade = true
      }
    })

    if (changesMade) {
      setListings(updatedListings)
      setPasteData("")
      notify("success", "Changes added to pending list.")
    } else {
      notify("info", "No new changes detected.")
    }
  }, [pasteData, azureListings, listings])

  const handleRemoveClicked = useCallback((id: string) => {
    setListings((prev) => prev.map((l) => (l.Id === id ? { ...l, reason: "Removal" } : l)))
  }, [])

  const handleCancelClicked = useCallback((id: string) => {
    setListings((prev) =>
      prev.reduce<Listing[]>((acc, l) => {
        if (l.Id === id) {
          if (l.reason === "Removal") delete l.reason
          else if (l.reason === "Addition") return acc
        }
        acc.push({ ...l })
        return acc
      }, [])
    )
  }, [])

  const handleApplyChanges = useCallback(() => {
    const changes = listings.filter((l) => l.reason)
    if (changes.length === 0) return notify("warning", "No changes to be made.")

    const updated = listings
      .filter((l) => l.reason !== "Removal")
      .map(({ listingName, Id }) => ({ listingName, Id }))

    if (confirm(`Apply [${changes.length}] listing changes to [${configName}]?`)) {
      saveConfig(updated, configName)
      resetState()
    }
  }, [listings, configName, saveConfig])

  const handleSelectSku = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setAddSku(e.target.value)
  }, [])

  const handleAddListing = () => {
    const match = azureListings.find((a) => a.amazonListingSku === addSku)
    if (!match) return

    const alreadyExists = listings.some((l) => l.listingName === match.amazonListingSku)
    if (alreadyExists) return notify("info", `${match.amazonListingSku} is already added.`)

    setListings((prev) => [...prev, { listingName: match.amazonListingSku, Id: match.listingId, reason: "Addition" }])
    setAddSku("")
  }

  const resetState = useCallback(() => {
    setListings([])
    setAddSku("")
    setPasteData("")
    closeModal()
  }, [closeModal])

  const modified = useMemo(() => listings.filter((l) => l.reason), [listings])

  return (
    <Modal show={visible} onHide={resetState} animation={false} size="xl" dialogClassName="custom-modal-xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{configName.split("/").pop() || "Listings"} Editor</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-no-scroll">
        <Container fluid className="d-flex flex-column gap-3 h-100">
          <Form.Group className="mb-3">
            <Form.Label>Add Listing by SKU</Form.Label>
            <Form.Select value={addSku} onChange={handleSelectSku}>
              <option value="">-- Type or select a SKU --</option>
              {azureListings.map((a) => (
                <option key={`${a.listingId}_${a.amazonListingSku}`} value={a.amazonListingSku}>
                  {a.amazonListingSku}
                </option>
              ))}
            </Form.Select>
            <Button className="mt-2" onClick={handleAddListing} disabled={!addSku}>
              Add Listing
            </Button>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Paste Listings by SKU (One per line)</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={pasteData}
              onChange={(e) => setPasteData(e.target.value)}
              placeholder="Paste SKUs here, one per line"
            />
            <Button variant="primary" onClick={handlePaste} className="mt-2">
              Parse Listings
            </Button>
          </Form.Group>

          <h5>Pending Changes [{modified.length}]</h5>
          <div className="table-responsive mb-4" style={{ maxHeight: "200px", overflowY: "auto" }}>
            <Table striped bordered hover size="sm" className="scrollable-table sticky-header">
              <thead className="table-light" style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2 }}>
                <tr>
                  <th>SKU</th>
                  <th>ID</th>
                  <th>Reason</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {modified.map((l) => (
                  <tr key={`${l.Id}_change`}>
                    <td>{l.listingName}</td>
                    <td>{l.Id}</td>
                    <td>{l.reason}</td>
                    <td className="text-center">
                      <Button size="sm" variant="danger" onClick={() => handleCancelClicked(l.Id)}>
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <h5>Current Listings [{listings.length}]</h5>
          <div className="table-responsive mb-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
            <Table striped bordered hover size="sm" className="scrollable-table sticky-header">
              <thead className="table-light" style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 2 }}>
                <tr>
                  <th>SKU</th>
                  <th>ID</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <tr key={`${l.Id}_list`}>
                    <td>{l.listingName}</td>
                    <td>{l.Id}</td>
                    <td className="text-center">
                      <Button size="sm" variant="danger" onClick={() => handleRemoveClicked(l.Id)} disabled={!!l.reason}>
                        {l.reason || "Remove"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={resetState}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleApplyChanges} disabled={modified.length === 0}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AmazonSFPConfigListingsEdit